/**
 * "Brainstorm with AI" — the panel that opens under a highlighted passage in
 * the script view. Ask anything about the highlight; the whole script rides
 * along as context (features/script/brainstorm.ts explains what gets sent).
 *
 * DELIBERATELY READ-ONLY. Nothing here writes to the board: no cell is
 * rewritten, no comment is created, no answer is stored. It is a conversation
 * you have next to the script and then close. That is what lets the answer be
 * free prose rather than a validated shape — there is nothing downstream to
 * corrupt — and it is why a wrong answer costs a re-ask instead of an undo.
 *
 * WHICH model answers is the writer's to choose (the shared picker in the
 * footer — features/ai-models, remembered per person in localStorage). Default
 * is the pre-selected option and usually means OpenRouter's router picks per
 * question, which is right for arbitrary questions; naming one matters when the
 * router keeps landing on a model that will not serve the call, and the panel
 * says so by name when that happens instead of "the AI provider failed to
 * answer".
 *
 * The exchange stays on screen and earlier turns are replayed to the model, so
 * "and what if we cut the second half?" means something. The transcript is
 * component state and dies with the popover: it is thinking-out-loud, and
 * persisting it would make everyone else on the board read one writer's
 * half-finished reasoning about a line they are also editing.
 *
 * TRANSLATE RESULT (owner's ask, 2026-09-03). The answer arrives in the
 * script's language by the system prompt's rule; the team may read another.
 * Each answer carries a language pick and a Translate button; the translation
 * lands under that answer, per turn, and is never replayed as history — it is
 * the same answer in other words, not a new turn. The pick is remembered
 * (translate-pick.ts), defaulting to the browser's language.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import {
  ApiError,
  askScriptBrainstorm,
  assetUrl,
  generateBrainstormVisual,
  translateBrainstormAnswer,
  type BrainstormTurn,
} from "../../core/api.js";
import { ModelSelect } from "../ai-models/ModelSelect.js";
import { DEFAULT_MODEL, rememberModel, storedModel } from "../ai-models/pick.js";
import { languageLabel, languageOptions } from "../translate/languages.js";
import {
  BRAINSTORM_MAX_HISTORY_TURNS,
  brainstormErrorMessage,
  translateErrorMessage,
  visualErrorMessage,
  type BrainstormContext,
} from "./brainstorm.js";
import { rememberTranslateLanguage, storedTranslateLanguage } from "./translate-pick.js";

/** Examples, shown once, so the empty box is not a blank stare. */
const PLACEHOLDER =
  "How would you make this easier to follow?  ·  What should we show on screen here?  ·  Does this land for a ten-year-old?";

export interface BrainstormPopoverProps {
  boardId: string;
  /** The highlight, frozen when the panel opened. */
  selection: readonly string[];
  /** Highlighted lines that did not fit the request's budget. */
  selectionDropped: number;
  /**
   * Re-read the script + highlight at ASK time. A function, not a value: the
   * board is live, and a follow-up asked five minutes later should be about
   * what the script says now, not what it said when the panel opened.
   */
  readContext: () => BrainstormContext;
  onClose: () => void;
}

/** One turn's optional visual — absent until the writer asks for one. */
type VisualState =
  | { status: "pending" }
  | {
      status: "done";
      assetId: string;
      w: number;
      h: number;
      model: string;
      /** The board's design theme shaped this frame. */
      themed: boolean;
    }
  | { status: "error"; message: string };

/** One turn's optional translation — absent until the writer asks for one. */
type TranslationState =
  | { status: "pending"; to: string }
  | { status: "done"; to: string; text: string; model: string }
  | { status: "error"; to: string; message: string };

interface Exchange extends BrainstormTurn {
  /** Which model answered — the auto router picks per question. */
  model: string;
  visual?: VisualState;
  translation?: TranslationState;
}

export function BrainstormPopover({
  boardId,
  selection,
  selectionDropped,
  readContext,
  onClose,
}: BrainstormPopoverProps) {
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<Exchange[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<string>(() => storedModel("script-brainstorm"));
  /** Target language for "Translate result" — one pick for the whole panel. */
  const [translateTo, setTranslateTo] = useState<string>(() => storedTranslateLanguage());
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  /** Guards a resolve that lands after the panel closed (no setState on unmount). */
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    inputRef.current?.focus();
    return () => {
      alive.current = false;
    };
  }, []);

  // Keep the newest answer in view as the transcript grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, pending]);

  const ask = async () => {
    const asked = question.trim();
    if (asked.length === 0 || pending) return;
    setPending(true);
    setError(null);
    // Cleared NOW, not on success: the question is already in flight, and a
    // box that still holds it invites a second identical send.
    setQuestion("");

    try {
      const context = readContext();
      if (context.selection.length === 0) {
        // The highlighted rows were emptied or deleted while the panel was open
        // (C4) — asking about nothing would spend a call to be told nothing.
        setError("The highlighted lines are gone. Close this and highlight again.");
        setQuestion(asked);
        return;
      }
      const result = await askScriptBrainstorm(boardId, {
        script: context.script,
        sampled: context.sampled,
        totalLines: context.totalLines,
        selection: context.selection,
        question: asked,
        // Oldest turns fall off rather than the newest: the recent exchange is
        // what a follow-up is following.
        history: turns
          .slice(-BRAINSTORM_MAX_HISTORY_TURNS)
          .map(({ question: q, answer }) => ({ question: q, answer })),
        // Omitted on Auto so the server resolves its own default — sending an
        // empty string would be a model id the provider has never heard of.
        ...(model === DEFAULT_MODEL ? {} : { model }),
      });
      if (!alive.current) return;
      setTurns((previous) => [
        ...previous,
        { question: asked, answer: result.answer, model: result.model },
      ]);
    } catch (failure) {
      if (!alive.current) return;
      setError(
        failure instanceof ApiError
          ? brainstormErrorMessage(failure.status, failure.code, modelFromError(failure))
          : "Couldn't reach the server. Try again.",
      );
      // Hand the question back so a failed ask is one click from a retry.
      setQuestion(asked);
    } finally {
      if (alive.current) setPending(false);
    }
  };

  /**
   * Render one frame from turn `index`'s answer. Per-turn, not per-panel: two
   * answers are two different ideas, and each earns its own picture. The
   * selection is re-read at generate time (same rule as `ask`) so the frame
   * grounds itself in what the script says now.
   */
  const generateVisual = async (index: number) => {
    const turn = turns[index];
    if (turn === undefined || turn.visual?.status === "pending") return;
    const setVisual = (visual: VisualState | undefined) =>
      setTurns((previous) =>
        previous.map((t, i) =>
          i === index ? { ...t, ...(visual === undefined ? {} : { visual }) } : t,
        ),
      );
    setVisual({ status: "pending" });
    try {
      const context = readContext();
      const result = await generateBrainstormVisual(boardId, {
        question: turn.question,
        answer: turn.answer,
        selection: context.selection.length > 0 ? context.selection : [...selection],
      });
      if (!alive.current) return;
      setVisual({
        status: "done",
        assetId: result.assetId,
        w: result.w,
        h: result.h,
        model: result.model,
        themed: result.themed,
      });
    } catch (failure) {
      if (!alive.current) return;
      setVisual({
        status: "error",
        message:
          failure instanceof ApiError
            ? visualErrorMessage(failure.status, failure.code)
            : "Couldn't reach the server. Try again.",
      });
    }
  };

  /**
   * Translate turn `index`'s answer into the panel's picked language. Per turn,
   * like the visual: the result lands under THAT answer, replacing an earlier
   * translation of it (a second pick is a correction, not a second reading).
   */
  const translateTurn = async (index: number) => {
    const turn = turns[index];
    if (turn === undefined || turn.translation?.status === "pending") return;
    const to = translateTo;
    const setTranslation = (translation: TranslationState) =>
      setTurns((previous) => previous.map((t, i) => (i === index ? { ...t, translation } : t)));
    setTranslation({ status: "pending", to });
    try {
      const result = await translateBrainstormAnswer(boardId, { text: turn.answer, to });
      if (!alive.current) return;
      setTranslation({ status: "done", to, text: result.text, model: result.model });
    } catch (failure) {
      if (!alive.current) return;
      setTranslation({
        status: "error",
        to,
        message:
          failure instanceof ApiError
            ? translateErrorMessage(failure.status, failure.code, modelFromError(failure))
            : "Couldn't reach the server. Try again.",
      });
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Grid/global shortcuts must never fire while typing a question here — the
    // same rule CommentInput follows.
    event.stopPropagation();
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void ask();
      return;
    }
    if (event.key === "Escape") onClose();
  };

  const quoted = selection.join("\n");

  return (
    <div className="script-popover script-brainstorm">
      <div className="script-brainstorm-head">
        <span className="script-brainstorm-title">
          <SparkleIcon /> Brainstorm with AI
        </span>
        <button
          type="button"
          className="script-brainstorm-close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className="script-popover-quote" title={quoted}>
        “{clip(quoted.replaceAll("\n", " ⏎ "), 160)}”
        <span className="script-brainstorm-meta">
          {selection.length === 1 ? "1 line" : `${selection.length} lines`}
          {selectionDropped > 0 && ` · ${selectionDropped} more not sent (too long)`}
          {" · the whole script is sent as context"}
        </span>
      </div>

      {turns.length > 0 && (
        <div className="script-brainstorm-thread" ref={scrollRef}>
          {turns.map((turn, i) => (
            <div key={i} className="script-brainstorm-turn">
              <div className="script-brainstorm-q">{turn.question}</div>
              <div className="script-brainstorm-a">{turn.answer}</div>
              <div className="script-brainstorm-model">{turn.model}</div>
              <div className="script-brainstorm-turn-actions">
                {turn.visual === undefined && (
                  <button
                    type="button"
                    className="script-brainstorm-visual-btn"
                    onClick={() => void generateVisual(i)}
                  >
                    <SparkleIcon /> Generate a visual for this?
                  </button>
                )}
                <div
                  className="script-brainstorm-translate"
                  title="Show this answer in another language"
                >
                  <span>Translate result</span>
                  <select
                    aria-label="Translate result into"
                    value={translateTo}
                    disabled={turn.translation?.status === "pending"}
                    onChange={(event) => {
                      setTranslateTo(event.target.value);
                      rememberTranslateLanguage(event.target.value);
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {languageOptions(translateTo, false).map((language) => (
                      <option key={language.tag} value={language.tag}>
                        {language.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn"
                    disabled={turn.translation?.status === "pending"}
                    onClick={() => void translateTurn(i)}
                  >
                    {turn.translation?.status === "pending"
                      ? "Translating…"
                      : turn.translation?.status === "done"
                        ? "Translate again"
                        : "Translate"}
                  </button>
                </div>
              </div>
              {turn.translation?.status === "done" && (
                <div className="script-brainstorm-translation" lang={turn.translation.to}>
                  {turn.translation.text}
                  <div className="script-brainstorm-model">
                    Translated to {languageLabel(turn.translation.to)} · {turn.translation.model}
                  </div>
                </div>
              )}
              {turn.translation?.status === "error" && (
                <div className="script-brainstorm-error">{turn.translation.message}</div>
              )}
              {turn.visual?.status === "pending" && (
                <div className="script-brainstorm-pending">
                  Rendering a frame — this takes a little while…
                </div>
              )}
              {turn.visual?.status === "error" && (
                <div className="script-brainstorm-visual-retry">
                  <span className="script-brainstorm-error">{turn.visual.message}</span>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => void generateVisual(i)}
                  >
                    Retry
                  </button>
                </div>
              )}
              {turn.visual?.status === "done" && (
                <figure className="script-brainstorm-visual">
                  <img
                    src={assetUrl(turn.visual.assetId)}
                    width={turn.visual.w}
                    height={turn.visual.h}
                    alt={`Generated visual: ${clip(turn.answer, 80)}`}
                  />
                  <figcaption className="script-brainstorm-model">
                    {turn.visual.themed
                      ? `${turn.visual.model} · matched to this board's design theme`
                      : `${turn.visual.model} · house style (no design theme on this board yet)`}
                  </figcaption>
                </figure>
              )}
            </div>
          ))}
        </div>
      )}

      {pending && <div className="script-brainstorm-pending">Thinking…</div>}
      {error !== null && <div className="script-brainstorm-error">{error}</div>}

      <textarea
        ref={inputRef}
        className="script-brainstorm-input"
        rows={2}
        value={question}
        placeholder={turns.length === 0 ? PLACEHOLDER : "Ask a follow-up…"}
        disabled={pending}
        onChange={(event) => setQuestion(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <div className="script-brainstorm-actions">
        <label className="script-brainstorm-model-pick" title="Which model answers">
          <span className="script-brainstorm-hint">Model</span>
          <ModelSelect
            functionKey="script-brainstorm"
            kind="text"
            value={model}
            disabled={pending}
            onChange={(next) => {
              setModel(next);
              rememberModel("script-brainstorm", next);
            }}
          />
        </label>
        <button
          type="button"
          className="btn primary"
          disabled={pending || question.trim().length === 0}
          onClick={() => void ask()}
        >
          {pending ? "Asking…" : "Ask"}
        </button>
      </div>
    </div>
  );
}

/** The AI mark — a two-star sparkle, the one glyph everyone reads as "AI". */
export function SparkleIcon() {
  return (
    <svg
      className="script-ai-icon"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6.2 1 7.35 4.25 10.6 5.4 7.35 6.55 6.2 9.8 5.05 6.55 1.8 5.4 5.05 4.25Z"
        fill="currentColor"
      />
      <path
        d="M11.9 8.3 12.6 10.3 14.6 11 12.6 11.7 11.9 13.7 11.2 11.7 9.2 11 11.2 10.3Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * The model the server says it tried, off an error body. `ApiError.body` is
 * `unknown` by contract (an error body is whatever the route sent), so this
 * narrows rather than casts — a route that sends no model just yields
 * undefined and the message stays generic.
 */
function modelFromError(failure: ApiError): string | undefined {
  const body = failure.body;
  if (typeof body !== "object" || body === null) return undefined;
  const model = (body as { model?: unknown }).model;
  return typeof model === "string" && model.length > 0 ? model : undefined;
}

function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
